import { z } from 'zod';
import { PRODUCT_LINES } from '@nava/product-lines';
import { VOICE_PROVIDERS } from '@nava/voice-providers';
import { createListResponseSchema } from './ListResponse.js';
import { createPaginatedListResponseSchema } from './PaginatedListResponse.js';
import { NonEmptyStringSchema, ProjectStatusSchema } from './common.js';
import { CreateProjectBrandBindingSchema } from './ProjectBrandBinding.js';
const STUDY_LOCALES = [
    'en-GB',
    'sv-SE',
    'nl-NL',
    'de-DE',
    'fr-FR',
    'es-ES',
    'da-DK',
    'fi-FI',
    'nb-NO',
    'pt-PT',
    'pl-PL',
    'cs-CZ',
    'ko-KR',
];
const STUDY_DEFAULT_MODERATOR_IDS = [
    'andrew',
    'anders',
    'daniel',
    'claudia',
    'marcel',
    'manuel',
    'soren',
    'mikko',
    'emma',
    'afonso',
    'tomasz',
    'miroslav',
    'harry',
];
const ProjectStatusFilterSchema = z.array(ProjectStatusSchema).optional();
export const ProjectSupportListSortBySchema = z.enum(['title', 'status', 'updatedAt', 'createdAt']);
export const SortDirectionSchema = z.enum(['asc', 'desc']);
const SupportDateTimeFilterSchema = z
    .string()
    .refine((value) => /^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isNaN(Date.parse(value)), 'Expected a date or datetime value');
/**
 * Parse a comma-separated `status` query string (e.g. `"completed,archived"`) into
 * a normalized array consumable by `ProjectsQuerySchema.status`. Returns `undefined`
 * for empty/missing input so the schema's default (no filter) kicks in.
 */
export function parseProjectStatusFilterParam(value) {
    if (!value)
        return undefined;
    const parts = value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    return parts.length > 0 ? parts : undefined;
}
export const ProjectStatusTransitionActorTypeSchema = z.enum(['user', 'ai', 'system']);
export const ProjectStatusTransitionActorOriginSchema = z.enum(['user', 'ai', 'system']);
export const ProjectStatusTransitionActorSchema = z
    .object({
    actorType: ProjectStatusTransitionActorTypeSchema,
    actorId: NonEmptyStringSchema,
    origin: ProjectStatusTransitionActorOriginSchema,
})
    .strict();
export const ProjectStatusTransitionReasonSchema = z
    .string()
    .trim()
    .min(1)
    .regex(/^[a-z0-9_]+$/);
export const ProjectHistoryActionSchema = z.enum([
    'project.status_changed',
    'project.sharing_invitation_created',
    'project.sharing_invitation_resent',
    'project.sharing_invitation_revoked',
    'project.sharing_member_role_updated',
    'project.sharing_member_removed',
    'project.sharing_self_removed',
    'project.analysis_run_requested',
]);
export const ProjectHistoryActorSchema = ProjectStatusTransitionActorSchema.extend({
    traceId: z.string().min(1).optional(),
}).strict();
export const ProjectHistoryStateSchema = z.record(z.string(), z.unknown());
export const AppendProjectHistoryEventRequestSchema = z
    .object({
    action: ProjectHistoryActionSchema,
    actor: ProjectHistoryActorSchema,
    target: ProjectHistoryStateSchema.nullable().optional(),
    metadata: ProjectHistoryStateSchema.optional(),
    previousState: ProjectHistoryStateSchema.nullable().optional(),
    nextState: ProjectHistoryStateSchema.nullable().optional(),
    occurredAt: z.string().datetime().optional(),
})
    .strict();
export const ProjectHistoryEventResponseSchema = z
    .object({
    id: z.string().guid(),
    projectId: z.string().guid(),
    organizationId: z.string().guid(),
    action: ProjectHistoryActionSchema,
    actor: ProjectHistoryActorSchema,
    target: ProjectHistoryStateSchema.nullable(),
    metadata: ProjectHistoryStateSchema,
    previousState: ProjectHistoryStateSchema.nullable(),
    nextState: ProjectHistoryStateSchema.nullable(),
    occurredAt: z.string().datetime(),
    createdAt: z.string().datetime(),
})
    .strict();
export const ProjectHistoryEventListResponseSchema = z
    .object({
    items: z.array(ProjectHistoryEventResponseSchema),
})
    .strict();
/** The Nava product line a project belongs to, from `@nava/product-lines`. */
export const ProductLineSchema = z.enum(PRODUCT_LINES);
/** The voice vendor a project's interviews run on, from `@nava/voice-providers`. */
export const VoiceProviderSchema = z.enum(VOICE_PROVIDERS);
export const CreateProjectRequestSchema = z
    .object({
    /** Optional stable ID for idempotent cross-service project creation. */
    id: z.string().guid().optional(),
    organizationId: NonEmptyStringSchema,
    createdByUserId: z.string().guid().nullable().optional(),
    ownerUserId: z.string().guid().nullable().optional(),
    title: NonEmptyStringSchema,
    description: z.string().optional(),
    isDemo: z.boolean().optional(),
    systemKind: z.enum(['listening_pulse']).optional(),
    /** Omitted means Insights. */
    productLine: ProductLineSchema.optional(),
    participantHostname: z.string().trim().min(1).max(255).optional(),
    /**
     * Fixed once at creation; omitted means ElevenLabs for new studies.
     * Explicit internal overrides remain supported. Never changed by the user.
     */
    voiceProvider: VoiceProviderSchema.optional(),
    defaultLocale: z.enum(STUDY_LOCALES).optional(),
    moderatorId: z.enum(STUDY_DEFAULT_MODERATOR_IDS).optional(),
    organisationName: z.string().optional(),
    /** Stored with the project in one transaction. Omitted means no binding row: no brand. */
    brandBinding: CreateProjectBrandBindingSchema.optional(),
    /** Omitted uses the organization default; null explicitly uses product defaults. */
    experienceProfileId: z.string().uuid().nullable().optional(),
})
    .strict();
export const UpdateProjectRequestSchema = z
    .object({
    productLine: ProductLineSchema.optional(),
    title: z.string().optional(),
    description: z.string().nullable().optional(),
    organizationId: z.string().optional(),
    ownerUserId: z.string().guid().nullable().optional(),
    interviewTarget: z.number().multipleOf(1).nullable().optional(),
    participantHostname: z.string().trim().min(1).max(255).nullable().optional(),
})
    .strict();
export const DeleteProjectRequestSchema = z
    .object({
    actorUserId: z.string().guid(),
})
    .strict();
export const ProjectIdParamsSchema = z
    .object({
    id: NonEmptyStringSchema,
})
    .strict();
export const ProjectsQuerySchema = z
    .object({
    organizationId: NonEmptyStringSchema,
    actorUserId: NonEmptyStringSchema.optional(),
    includeShared: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(false)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
    includeOrganizationProjects: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(true)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
    limit: z.coerce.number().multipleOf(1).positive().max(100).prefault(20),
    offset: z.coerce.number().multipleOf(1).nonnegative().prefault(0),
    search: z.string().trim().min(1).max(120).optional(),
    status: ProjectStatusFilterSchema,
    retentionCutoff: z.string().datetime().optional(),
    retentionHideLaunched: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(false)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
})
    .strict();
export const ProjectsCountsQuerySchema = z
    .object({
    organizationId: NonEmptyStringSchema,
    actorUserId: NonEmptyStringSchema.optional(),
    includeShared: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(false)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
    includeOrganizationProjects: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(true)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
    retentionCutoff: z.string().datetime().optional(),
    retentionHideLaunched: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(false)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
})
    .strict();
export const ProjectsCountsResponseSchema = z
    .object({
    draft: z.number().multipleOf(1).nonnegative(),
    in_progress: z.number().multipleOf(1).nonnegative(),
    completed: z.number().multipleOf(1).nonnegative(),
    archived: z.number().multipleOf(1).nonnegative(),
    total: z.number().multipleOf(1).nonnegative(),
})
    .strict();
export const ProjectOwnerCountsRequestSchema = z
    .object({
    userIds: z.array(z.string().guid()).max(100),
    excludeDemo: z.boolean().optional(),
})
    .strict();
export const ProjectOwnerCountsResponseSchema = z
    .object({
    counts: z.record(z.string().guid(), z.number().multipleOf(1).nonnegative()),
})
    .strict();
export const ProjectAccessCountRequestSchema = z
    .object({
    actorUserId: z.string().guid(),
    memberOrganizationIds: z.array(z.string().guid()).max(100).prefault([]),
    implicitOrganizationIds: z.array(z.string().guid()).max(100).prefault([]),
    excludeOwned: z.boolean().prefault(false),
    excludeDemo: z.boolean().prefault(false),
})
    .strict();
export const ProjectAccessCountResponseSchema = z
    .object({
    count: z.number().multipleOf(1).nonnegative(),
})
    .strict();
export const ProjectAccessListRequestSchema = ProjectAccessCountRequestSchema.extend({
    limit: z.coerce.number().multipleOf(1).positive().max(100).prefault(20),
    offset: z.coerce.number().multipleOf(1).nonnegative().prefault(0),
    status: ProjectStatusFilterSchema,
    organizationId: z.string().guid().optional(),
    updatedFrom: SupportDateTimeFilterSchema.optional(),
    updatedTo: SupportDateTimeFilterSchema.optional(),
    sortBy: ProjectSupportListSortBySchema.prefault('updatedAt'),
    sortDirection: SortDirectionSchema.prefault('desc'),
}).strict();
export const ProjectSupportListQuerySchema = z
    .object({
    limit: z.coerce.number().multipleOf(1).positive().max(100).prefault(20),
    offset: z.coerce.number().multipleOf(1).nonnegative().prefault(0),
    search: z.string().trim().min(1).max(120).optional(),
    status: ProjectStatusFilterSchema,
    organizationId: z.string().guid().optional(),
    ownerUserId: z.string().guid().optional(),
    updatedFrom: SupportDateTimeFilterSchema.optional(),
    updatedTo: SupportDateTimeFilterSchema.optional(),
    sortBy: ProjectSupportListSortBySchema.prefault('updatedAt'),
    sortDirection: SortDirectionSchema.prefault('desc'),
    excludeDemo: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .prefault(false)
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true')),
})
    .strict();
export const ProjectResponseSchema = z
    .object({
    id: z.string(),
    organizationId: z.string(),
    createdByUserId: z.string().guid().nullable().optional(),
    ownerUserId: z.string().guid().nullable().optional(),
    title: z.string(),
    description: z.string().nullable(),
    status: ProjectStatusSchema,
    isDemo: z.boolean(),
    systemKind: z.string().nullable().optional(),
    productLine: ProductLineSchema.optional(),
    participantHostname: z.string().nullable().optional(),
    /** Omitted by older research-data builds; readers treat that as VAPI. */
    voiceProvider: VoiceProviderSchema.optional(),
    interviewTarget: z.number().multipleOf(1).nullable().optional(),
    launchedAt: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ProjectSupportResponseSchema = ProjectResponseSchema.extend({
    deletedAt: z.string().datetime().nullable(),
}).strict();
export const UpdateProjectStatusRequestSchema = z
    .object({
    status: ProjectStatusSchema,
    reason: ProjectStatusTransitionReasonSchema.nullable().optional(),
    actor: ProjectStatusTransitionActorSchema.optional(),
})
    .strict();
export const AccessControlEntryTypeSchema = z.enum(['user', 'team', 'org', 'public']);
export const AccessControlPermissionSchema = z.enum([
    'read',
    'write',
    'delete',
    'share',
    'publish',
]);
export const AccessControlEntrySchema = z
    .object({
    type: AccessControlEntryTypeSchema,
    id: NonEmptyStringSchema,
    permissions: z.array(AccessControlPermissionSchema).min(1),
})
    .strict();
export const AccessControlSchema = z
    .object({
    entries: z.array(AccessControlEntrySchema),
})
    .strict();
export const ProjectMembershipRoleSchema = z.enum([
    'ProjectViewer',
    'ProjectEditor',
    'ProjectPublisher',
]);
export const ProjectAccessControlRequestSchema = z
    .object({
    actorUserId: NonEmptyStringSchema,
    subjectType: z.enum(['user']),
    subjectId: NonEmptyStringSchema,
    role: ProjectMembershipRoleSchema,
    idempotencyKey: NonEmptyStringSchema.optional(),
})
    .strict();
export const ProjectAccessControlUserRoleUpdateRequestSchema = z
    .object({
    actorUserId: NonEmptyStringSchema,
    role: ProjectMembershipRoleSchema,
    idempotencyKey: NonEmptyStringSchema.optional(),
})
    .strict();
export const ProjectAccessControlResponseSchema = z
    .object({
    projectId: NonEmptyStringSchema,
    accessControl: AccessControlSchema,
})
    .strict();
export const ProjectListResponseSchema = createListResponseSchema(ProjectResponseSchema);
export const ProjectPaginatedListResponseSchema = createPaginatedListResponseSchema(ProjectResponseSchema);
export const ProjectSupportPaginatedListResponseSchema = createPaginatedListResponseSchema(ProjectSupportResponseSchema);
//# sourceMappingURL=Project.js.map